import { useParams, Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext } from 'react'
import { lookupProjects, type ProjectLookup } from '../../api/projects'
import { Avatar, cn } from '../../design'

type ProjectCtx = { project: ProjectLookup }
const ProjectContext = createContext<ProjectCtx | null>(null)

export function useProject(): ProjectLookup {
  const ctx = useContext(ProjectContext)
  if (!ctx) throw new Error('useProject must be used inside a project route')
  return ctx.project
}

export default function ProjectLayout() {
  const { projectKey } = useParams<{ projectKey: string }>()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const projectsQ = useQuery({
    queryKey: ['lookups', 'projects'],
    queryFn: lookupProjects,
    staleTime: 60_000,
  })

  if (projectsQ.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <div className="h-6 w-6 rounded-full border-2 border-border border-t-accent animate-spin" />
      </div>
    )
  }

  const project = projectsQ.data?.find(p => p.key === projectKey)
  if (!project) {
    return (
      <div className="mx-auto max-w-xl px-6 py-16 text-center">
        <h1 className="text-xl font-semibold">Project not found</h1>
        <p className="text-sm text-fg-subtle mt-2">
          No project with key <code className="px-1 rounded bg-bg-muted">{projectKey}</code>.
        </p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-sm text-accent hover:underline"
        >
          Go home
        </button>
      </div>
    )
  }

  // Sub-tab inferred from URL — placed in project header for in-page nav
  const subPath = pathname.replace(`/projects/${projectKey}`, '').replace(/^\//, '') || 'dashboard'

  return (
    <ProjectContext.Provider value={{ project }}>
      <div className="flex flex-col h-full">
        <header className="border-b border-border bg-bg-elevated px-6 py-3">
          <div className="flex items-center gap-3">
            <Avatar name={project.name} color={project.color ?? '#d97757'} size="md" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs text-fg-subtle font-mono">
                <span>{project.key}</span>
                <span>·</span>
                <span className="capitalize">{subPath.replace('-', ' ')}</span>
              </div>
              <h1 className="text-lg font-semibold tracking-tight truncate">{project.name}</h1>
            </div>
          </div>
        </header>
        <div className={cn('flex-1 min-h-0', subPath === 'board' || subPath === 'timeline' ? 'overflow-hidden' : 'overflow-auto')}>
          <Outlet />
        </div>
      </div>
    </ProjectContext.Provider>
  )
}
